import React, { useState, useEffect } from 'react';
import AdminLayout from './AdminLayout';
import {
  Archive,
  Search,
  Filter,
  Eye,
  Calendar,
  User,
  FileText,
  Loader2,
  X,
  Building,
  Tag,
} from 'lucide-react';
import { lookupAPI } from '../../services/api';
import { resolveServerUrl } from '../../services/api';
import mammoth from 'mammoth';

// Types
interface ArchivedWork {
  id: number;
  title: string;
  description: string;
  file_name: string;
  file_path: string;
  file_type: string;
  submitted_date: string;
  reviewed_date: string;
  faculty_id: number;
  faculty_name: string;
  department_name: string | null;
  category_id: number;
  category_name: string;
}

interface Category {
  id: number;
  name: string;
  description: string;
  is_active: boolean;
}

export default function ArchivePage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [archivedWorks, setArchivedWorks] = useState<ArchivedWork[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedWork, setSelectedWork] = useState<ArchivedWork | null>(null);
  const [docxPreviewHtml, setDocxPreviewHtml] = useState('');
  const [docxPreviewLoading, setDocxPreviewLoading] = useState(false);
  const [docxPreviewError, setDocxPreviewError] = useState('');

  // Handle view click
  const handleViewClick = (work: ArchivedWork) => {
    setSelectedWork(work);
    setViewModalOpen(true);
  };

  const handleCloseModal = () => {
    setViewModalOpen(false);
    setSelectedWork(null);
  };

  const getFileExtension = (work: ArchivedWork) => {
    const source = work.file_name || work.file_path || '';
    const dotIndex = source.lastIndexOf('.');
    if (dotIndex < 0) return '';
    return source.slice(dotIndex + 1).toLowerCase();
  };

  const isImageFile = (work: ArchivedWork) => {
    const ext = getFileExtension(work);
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext) || work.file_type?.startsWith('image/');
  };

  const isPdfFile = (work: ArchivedWork) => getFileExtension(work) === 'pdf';
  const isVideoFile = (work: ArchivedWork) => ['mp4', 'webm', 'ogg', 'mov'].includes(getFileExtension(work));
  const isAudioFile = (work: ArchivedWork) => ['mp3', 'wav', 'ogg', 'm4a'].includes(getFileExtension(work));
  const isDocxFile = (work: ArchivedWork) => getFileExtension(work) === 'docx';
  const selectedWorkFileUrl = selectedWork ? resolveServerUrl(selectedWork.file_path) : '';
  const selectedWorkIsDocx = selectedWork ? isDocxFile(selectedWork) : false;

  useEffect(() => {
    const generateDocxPreview = async () => {
      if (!viewModalOpen || !selectedWork || !selectedWork.file_path || !selectedWorkIsDocx) {
        setDocxPreviewHtml('');
        setDocxPreviewError('');
        setDocxPreviewLoading(false);
        return;
      }

      try {
        setDocxPreviewLoading(true);
        setDocxPreviewError('');
        const response = await fetch(selectedWorkFileUrl);
        const arrayBuffer = await response.arrayBuffer();
        const result = await mammoth.convertToHtml({ arrayBuffer });
        setDocxPreviewHtml(result.value);
      } catch (err) {
        console.error('Failed to load DOCX preview:', err);
        setDocxPreviewHtml('');
        setDocxPreviewError('Preview not available for this DOCX file.');
      } finally {
        setDocxPreviewLoading(false);
      }
    };

    generateDocxPreview();
  }, [viewModalOpen, selectedWork, selectedWorkFileUrl, selectedWorkIsDocx]);

  // Fetch data from database
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch categories
        const catResponse = await lookupAPI.getCategories();
        console.log('Categories response:', catResponse);
        
        if (catResponse.categories) {
          setCategories(catResponse.categories);
        } else if (catResponse.data && Array.isArray(catResponse.data)) {
          if (Array.isArray(catResponse.data[0])) {
            setCategories(catResponse.data[0]);
          } else {
            setCategories(catResponse.data);
          }
        } else if (Array.isArray(catResponse)) {
          setCategories(catResponse);
        }

        // Fetch archived works
        await fetchArchivedWorks();
      } catch (err: any) {
        console.error('Error fetching data:', err);
        setError(err.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Fetch archived works (reusable)
  const fetchArchivedWorks = async (keyword?: string, category_id?: number) => {
    try {
      const response = await lookupAPI.getArchive(
        keyword || undefined,
        category_id || undefined,
        undefined,
        1,
        100
      );
      console.log('Archive response:', response);

      // Handle the stored procedure response format: data = [[works], [{total_count}]]
      if (response.data && Array.isArray(response.data)) {
        const data = response.data;
        if (Array.isArray(data) && data.length >= 1 && Array.isArray(data[0])) {
          setArchivedWorks(data[0] || []);
          if (data[1] && data[1][0]) {
            setTotalCount(data[1][0].total_count || data[0].length);
          } else {
            setTotalCount(data[0].length);
          }
        } else if (Array.isArray(data)) {
          setArchivedWorks(data);
          setTotalCount(data.length);
        }
      } else if (response.archive) {
        setArchivedWorks(response.archive);
        setTotalCount(response.total_count || response.archive.length);
      } else if (Array.isArray(response)) {
        setArchivedWorks(response);
        setTotalCount(response.length);
      }
    } catch (err: any) {
      console.error('Error fetching archive:', err);
    }
  };

  // Filter works based on search and category (client-side filtering for quick response)
  const filteredWorks = archivedWorks.filter((work) => {
    const title = work.title || '';
    const facultyName = work.faculty_name || '';
    const categoryName = work.category_name || '';
    
    const matchesSearch =
      title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      facultyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      categoryName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || work.category_id?.toString() === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  // Calculate category stats from actual data
  const categoryStats = categories
    .map((cat) => ({
      category: cat.name,
      count: archivedWorks.filter((work) => work.category_id === cat.id).length,
    }))
    .filter((stat) => stat.count > 0)
    .sort((a, b) => b.count - a.count);

  // Calculate unique contributors
  const uniqueContributors = new Set(archivedWorks.map((work) => work.faculty_id)).size;
  const usedCategories = new Set(archivedWorks.map((work) => work.category_id)).size;

  return (
    <AdminLayout>
      {/* View Modal */}
      {viewModalOpen && selectedWork && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={handleCloseModal} />
          <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Submission Details</h2>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-6">
              {/* Title */}
              <div>
                <h3 className="text-2xl font-bold text-gray-900">{selectedWork.title}</h3>
                <span className="inline-flex items-center px-3 py-1 mt-2 rounded-full text-sm font-medium bg-purple-100 text-purple-700">
                  <Tag className="w-4 h-4 mr-1" />
                  {selectedWork.category_name}
                </span>
              </div>

              {/* Description */}
              {selectedWork.description && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-500 uppercase mb-2">Description</h4>
                  <p className="text-gray-700 whitespace-pre-wrap">{selectedWork.description}</p>
                </div>
              )}

              {/* Details Grid */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center text-gray-600 mb-1">
                    <User className="w-4 h-4 mr-2" />
                    <span className="text-sm font-medium">Faculty</span>
                  </div>
                  <p className="text-gray-900 font-semibold">{selectedWork.faculty_name}</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center text-gray-600 mb-1">
                    <Building className="w-4 h-4 mr-2" />
                    <span className="text-sm font-medium">Department</span>
                  </div>
                  <p className="text-gray-900 font-semibold">{selectedWork.department_name || 'Not assigned'}</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center text-gray-600 mb-1">
                    <Calendar className="w-4 h-4 mr-2" />
                    <span className="text-sm font-medium">Submitted</span>
                  </div>
                  <p className="text-gray-900 font-semibold">
                    {selectedWork.submitted_date ? new Date(selectedWork.submitted_date).toLocaleDateString() : 'N/A'}
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center text-gray-600 mb-1">
                    <Calendar className="w-4 h-4 mr-2" />
                    <span className="text-sm font-medium">Approved</span>
                  </div>
                  <p className="text-gray-900 font-semibold">
                    {selectedWork.reviewed_date ? new Date(selectedWork.reviewed_date).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
              </div>

              {/* File Info */}
              {selectedWork.file_name && (
                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="flex items-center">
                    <FileText className="w-5 h-5 text-purple-600 mr-3" />
                    <div>
                      <p className="font-medium text-gray-900">{selectedWork.file_name}</p>
                      <p className="text-sm text-gray-500">{selectedWork.file_type || 'File'}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* File Preview */}
              {selectedWork.file_path && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-500 uppercase mb-2">Preview</h4>
                  <div className="rounded-lg overflow-hidden border border-gray-200">
                    {isImageFile(selectedWork) ? (
                      <img
                        src={selectedWorkFileUrl}
                        alt={selectedWork.title}
                        className="w-full h-auto max-h-96 object-contain bg-gray-100"
                      />
                    ) : isPdfFile(selectedWork) ? (
                      <iframe
                        src={selectedWorkFileUrl}
                        title={selectedWork.title}
                        className="w-full h-[70vh] bg-white"
                      />
                    ) : isVideoFile(selectedWork) ? (
                      <video controls className="w-full max-h-96 bg-black">
                        <source src={selectedWorkFileUrl} />
                        Your browser does not support the video tag.
                      </video>
                    ) : isAudioFile(selectedWork) ? (
                      <div className="p-6 bg-gray-50 flex items-center justify-center">
                        <audio controls className="w-full max-w-xl">
                          <source src={selectedWorkFileUrl} />
                          Your browser does not support the audio tag.
                        </audio>
                      </div>
                    ) : selectedWorkIsDocx && !docxPreviewLoading && !docxPreviewError ? (
                      <div className="p-6 bg-white prose max-w-none">
                        <div dangerouslySetInnerHTML={{ __html: docxPreviewHtml }} />
                      </div>
                    ) : selectedWorkIsDocx && docxPreviewLoading ? (
                      <div className="p-6 text-center text-gray-600">Loading DOCX preview...</div>
                    ) : (
                      <div className="p-6 text-center text-gray-600">
                        {docxPreviewError || 'Preview not available for this file type.'}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4">
              <button
                onClick={handleCloseModal}
                className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Creative Works Archive</h1>
            <p className="text-gray-600 mt-1">Browse and manage all approved creative works</p>
          </div>
          <div className="mt-4 sm:mt-0">
            <div className="inline-flex items-center px-4 py-2 bg-purple-50 text-purple-700 rounded-lg border border-purple-200">
              <Archive className="w-5 h-5 mr-2" />
              <span className="font-semibold">{totalCount} Archived Works</span>
            </div>
          </div>
        </div>

        {/* Category Stats */}
        {categoryStats.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {categoryStats.map((stat) => (
              <div
                key={stat.category}
                className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-lg transition-shadow cursor-pointer"
              >
                <div className="text-2xl font-bold text-gray-900 mb-1">{stat.count}</div>
                <div className="text-sm text-gray-600">{stat.category}</div>
              </div>
            ))}
          </div>
        )}

        {/* Search and Filter */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by title, faculty, or category..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Filter className="h-5 w-5 text-gray-400" />
              </div>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="block w-full pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent appearance-none bg-white"
              >
                <option value="all">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id.toString()}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-purple-600 mx-auto mb-4" />
            <p className="text-gray-600">Loading archived works...</p>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="bg-red-50 rounded-xl border border-red-200 p-6 text-center">
            <p className="text-red-600">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Retry
            </button>
          </div>
        )}

        {/* Archive Grid */}
        {!loading && !error && filteredWorks.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredWorks.map((work) => (
              <div
                key={work.id}
                className="bg-white rounded-xl border border-gray-200 hover:shadow-lg transition-shadow overflow-hidden group"
              >
                {/* Card Header */}
                <div className="h-40 bg-gradient-to-br from-blue-500 to-purple-600 relative">
                  {work.file_path && work.file_type?.startsWith('image/') ? (
                    <img
                      src={resolveServerUrl(work.file_path)}
                      alt={work.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-16 h-16 bg-white bg-opacity-20 rounded-2xl backdrop-blur-sm flex items-center justify-center">
                        <FileText className="w-8 h-8 text-white" />
                      </div>
                    </div>
                  )}
                  <div className="absolute top-4 right-4">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-white bg-opacity-90 text-gray-900">
                      {work.category_name}
                    </span>
                  </div>
                </div>

                {/* Card Body */}
                <div className="p-6">
                  <h3 className="font-bold text-gray-900 mb-2 line-clamp-2">{work.title}</h3>
                  <div className="space-y-2 text-sm text-gray-600 mb-4">
                    <div className="flex items-center">
                      <User className="w-4 h-4 mr-2 flex-shrink-0" />
                      <span className="truncate">{work.faculty_name}</span>
                    </div>
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 mr-2 flex-shrink-0" />
                      <span>Archived: {work.reviewed_date ? new Date(work.reviewed_date).toLocaleDateString() : 'N/A'}</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 pt-4 border-t border-gray-200">
                    <button 
                      onClick={() => handleViewClick(work)}
                      className="w-full inline-flex items-center justify-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Click to View
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : !loading && !error && (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Archive className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">No archived works found</h3>
            <p className="text-gray-600">Try adjusting your search or filter criteria</p>
          </div>
        )}

        {/* Summary Stats */}
        {totalCount > 0 && (
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl p-8 text-white">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold mb-2">Archive Summary</h3>
              <p className="text-purple-100">
                A collection of excellence from BISU faculty members
              </p>
            </div>
            <div className="grid sm:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold mb-1">{totalCount}</div>
                <div className="text-purple-100">Total Works</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold mb-1">{uniqueContributors}</div>
                <div className="text-purple-100">Contributors</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold mb-1">{usedCategories}</div>
                <div className="text-purple-100">Categories</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
